/**
 * One fetch wrapper and one hook. That is the whole data layer, deliberately.
 *
 * The agent repo this grew out of has a "no framework" rule, written about a single
 * screen with a state machine you can hold in your head. This is seven screens with
 * tables, filters and date ranges, so React earns its place - but the discipline behind
 * that rule does not go away: no component-library kitchen sink, and no state manager
 * for what is a cache of GET requests.
 */

import { useCallback, useEffect, useRef, useState } from "react";

// Empty in production, because the Worker is mounted on the same hostname as this site.
// The session cookie is `__Host-` prefixed, which means Path=/ and NO Domain attribute:
// it is readable on exactly the origin that set it. Split the SPA and the API across two
// hostnames and the dashboard is signed in while every request it makes is not.
const BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

export async function api<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    // Sends the session cookie when the API is on a different origin in development.
    credentials: "include",
    headers: { Accept: "application/json" },
    signal,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(response.status, body?.error ?? `Request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

/**
 * Every write in the product.
 *
 * `credentials: "include"` for the cookie, and no custom header: the Worker's CSRF
 * defence is `SameSite=Lax` plus an `Origin` check, both of which a plain same-origin
 * POST satisfies. Adding a token header here would be a third mechanism guarding the
 * same door.
 */
export async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body ?? {}),
  });

  if (!response.ok) {
    const failure = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(response.status, failure?.error ?? `Request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

/**
 * A binary GET, and the only response in this product that is not JSON.
 *
 * Its own function rather than a flag on `api()` because the failure shape is different:
 * there is no `{error}` body to read a sentence out of, so the caller gets the status and
 * writes its own. Used by every Play button in the product: the opening line's WAV, a voice
 * sample, and a whole call's MP3.
 *
 * `audio/*` RATHER THAN A NAMED TYPE. It said `audio/wav` when the greeting was the only
 * thing anybody could play, and a call recording is an MP3 - so the header would have been a
 * lie that no server checks, which is the worst kind.
 */
export async function blob(path: string): Promise<Blob> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "audio/*" },
  });
  if (!response.ok) throw new ApiError(response.status, `Request failed (${response.status})`);
  return response.blob();
}

export interface Loaded<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  reload: () => void;
}

/**
 * GET a path, and abort the previous request when the path changes.
 *
 * The abort is the part worth having: typing in the search box fires a request per
 * keystroke, and without it the answer to "ste" can arrive after the answer to "steven"
 * and overwrite it. That bug is intermittent, looks like a caching problem, and is
 * genuinely unpleasant to find later.
 */
export function useApi<T>(path: string | null): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    if (path === null) {
      setData(null);
      setLoading(false);
      return;
    }

    controller.current?.abort();
    const current = new AbortController();
    controller.current = current;

    setLoading(true);
    api<T>(path, current.signal)
      .then((result) => {
        if (current.signal.aborted) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (current.signal.aborted) return;
        setError(
          err instanceof ApiError ? err : new ApiError(0, (err as Error).message ?? "Failed"),
        );
      })
      .finally(() => {
        if (!current.signal.aborted) setLoading(false);
      });

    return () => current.abort();
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

// --- what the API returns -------------------------------------------------------------

export interface Money {
  minor: number;
  basis: string | null;
  currency: string | null;
}

export interface Outcome {
  kind: string | null;
  label: string | null;
  group: string | null;
  resolved: boolean | null;
  handoff: string | null;
  priority: string | null;
  headline: string | null;
  value: Money | null;
  contact: { name: string | null; location: string | null };
}

/**
 * What the platform read off the transcript. THE THIRD ROW of README.md's table, so none
 * of it differs per agent - unlike `Outcome`, which is the agent author's own grade.
 */
export interface Analysis {
  status: string;
  summary: string | null;
  /**
   * What the caller wanted, as a label under fifty characters: "Battery price, Audi A4".
   * The line under a call in both views of /calls, where `summary` is the pane's.
   */
  ask: string | null;
  sentiment: string | null;
  /** `null` means nobody has read this call for a lead at all. It is NOT `cold`. */
  qualification: Qualification | null;
}

export type Band =
  | "hot"
  | "warm"
  | "cool"
  | "cold"
  /** Nobody said what they wanted. Deliberately not `cold` - see `qualify` on the Worker. */
  | "unscorable"
  | "not_a_fit"
  | "not_an_enquiry";

export interface Measure {
  dimension: string;
  /** none | low | medium | high | unknown. Unknown is not a low score. */
  level: string;
  note: string | null;
}

export interface Qualification {
  band: Band;
  /** Null on the three bands that are not temperatures: the two gates and the floor. */
  score: number | null;
  /**
   * Carried BESIDE the score and never folded into it. A strong enquiry we could not
   * capture means chase the customer and fix the agent; a weak one captured perfectly
   * means do nothing. Averaging the two would describe neither.
   */
  capture: "good" | "partial" | "none" | null;
  /** The five measures. Present on the detail page only - the list has no column for them. */
  dimensions?: Measure[];
}

export interface CallSummary {
  id: number;
  callId: string;
  agentId: string;
  organizationId: string;
  mode: string;
  startedAt: number;
  durationMs: number | null;
  channel: string | null;
  direction: string | null;
  endedBy: string | null;
  caller: string | null;
  /**
   * Who was rung, on a call the agent PLACED. Null on every inbound call.
   *
   * On an outbound call `caller` is the business's own number and this is the customer's,
   * which is the one a human reading the row would dial back - so the two are not
   * interchangeable and the row shows whichever the direction makes true.
   */
  callee: string | null;
  turns: number | null;
  outcome: Outcome;
  /**
   * What the call sounded like, or null - which is every call taken before recordings
   * existed, every call for a business that has recording switched off, and any call whose
   * upload did not land in the seconds a pod had left.
   *
   * ON THE LIST ROW AND NOT ONLY ON THE DETAIL, so `/calls` can draw a play button without a
   * second request per row. There is no URL in it: the player builds
   * `/v1/calls/:id/recording?v=<sha256>`, and the Worker resolves the call under the
   * reader's own access and finds the object itself.
   *
   * NO FIELD HERE IS CALLED `sampleRate`, deliberately. `redactForMember` drops any key
   * matching /cost|margin|micros|rate/i from a member's response, twelve levels deep and
   * with no error anywhere - the same trap `OpeningLineBlock` documents.
   */
  recording: { sha256: string; bytes: number; durationMs: number; recordedAt: number } | null;
  analysis: Analysis;
  /**
   * Whether this call is one a person has to look at: urgent, unresolved, or escalated.
   *
   * THE AGENT'S VERDICT, NOT THE PERSON'S, and computed by the Worker from the same three
   * columns the overview's "Needs your attention" counts, so a row and the number above the
   * list cannot disagree about which calls those are. It does not change when somebody marks
   * the call handled; `handled` is what changes.
   */
  needsPerson: boolean;
  /**
   * A PERSON'S MARK, or null. Somebody rang them back, booked them in, sorted it out at the
   * counter, and said so here.
   *
   * SEPARATE FROM `outcome.resolved` ON PURPOSE. That is the agent's own grade of how the
   * call went and is never rewritten; this is what happened afterwards, and a call can be
   * unresolved by the agent and handled by a person, which is exactly the case this exists
   * for. `by` is a name snapshotted when it was marked, not a join, so it still reads right
   * after that person leaves the team. `note` is optional and says what was done.
   */
  handled: { at: number; by: string | null; note: string | null } | null;
  /** The contact this call is filed under, or null. See the Contacts page. */
  contactId: string | null;
}

export interface Turn {
  role: "caller" | "agent";
  text: string;
  atMs: number | null;
  interrupted?: boolean;
}

export interface TimelineEvent {
  atMs: number;
  kind: "tool" | "node" | "handoff" | "error";
  name: string;
  args: Record<string, unknown> | null;
  result: Record<string, unknown> | null;
}

/**
 * One call, in full.
 *
 * NOTHING HERE IS OPTIONAL ANY MORE, and that is the shape of a real change rather than a
 * tidy-up. Four fields used to be - `externalId`, `endReason`, `errors` and a `diagnostics`
 * block - because the Worker ran a wider statement for an internal reader and a narrower
 * one for a member, and absent-not-null was how the member's response said so. There is
 * one statement now, it names no cost, and every reader of this page gets the same call.
 */
export interface CallDetail extends CallSummary {
  endedAt: number | null;
  interruptions: number | null;
  contact: { name: string | null; phone: string | null; location: string | null };
  /**
   * The contact this call is filed under, by the name it has NOW - not `contact` above,
   * which is what the agent took down on this one call.
   */
  contactRef: { id: string; name: string | null } | null;
  /**
   * The whole reading, not just the three fields the list needs.
   *
   * `followUp` is the analyser's one instruction for whoever picks the call up, and the
   * detail page is the only screen that has ever had room for it. The list keeps the
   * narrow `Analysis` because a table has no use for a sentence it cannot show.
   */
  analysis: Analysis & {
    followUp?: string | null;
    agentIssues?: string[];
    /** Every reason the caller rang, main one first. Empty for a call nobody has read. */
    intents?: string[];
    /** Strings; an object on a call read before the questions lost their topics. */
    unanswered?: Array<string | { question: string }>;
  };
  outcomeDetail: {
    card?: string | null;
    fields?: Record<string, unknown>;
    tags?: string[];
    unanswered?: string[];
  } | null;
  transcript: Turn[];
  timeline: TimelineEvent[];
  /**
   * What a hosted agent's tools did, as the dashboard ran them - the arguments it was handed
   * and what the model got back. Empty for every custom agent's call, whose tools are its
   * own code and show on `timeline` instead.
   */
  toolCalls?: ToolCall[];
}

// --- the owner-facing screens ----------------------------------------------------------
//
// Nothing below carries a cost, a rate or a margin, and that is not an oversight to be
// corrected later. The queries behind these two endpoints do not select `cost_micros`,
// so there is nothing here to type - which is the whole design of the gate.

export interface Vocabulary {
  /** The business's own noun for a good outcome: "lead", "booking", "job". */
  noun: string;
  nounPlural: string;
  /** Display overrides. Anything absent is humanised from the raw label. */
  labels: Record<string, string>;
  groups: Record<string, string>;
  /** Which groups this business counts as a good outcome. */
  successGroups: string[];
  intents: string[];
  /**
   * One line of "what counts as this" per intent key, and the one part of the
   * vocabulary the ANALYSER reads rather than a screen. Sparse on purpose: an owner writes
   * two of these for the categories that keep being confused and leaves the rest bare.
   */
  descriptions: Record<string, string>;
  /**
   * What each SENTIMENT means, by sentiment key, and the second part the ANALYSER reads.
   * Sparse in the other direction from `descriptions`: an absent key is not "no
   * definition", it is "the platform's own wording" - see `SENTIMENT_DEFINITIONS` in
   * `components/Sentiments.tsx`.
   */
  sentiments: Record<string, string>;
}

/** One row of the category editor. No `key` means new, and the Worker slugs one. */
export interface CategoryInput {
  key: string | null;
  label: string;
  description?: string;
}

/**
 * What `POST /v1/settings/agent` takes under `taxonomy`.
 *
 * AN ABSENT LIST IS A LIST UNCHANGED, the same partial-body rule the rest of that endpoint
 * follows. `merges` is not optional decoration: the Worker refuses an edit that drops a
 * category without saying where its calls go, because the alternative is a bar on the
 * overview for a category the settings screen says does not exist.
 */
export interface TaxonomyEdit {
  intents?: CategoryInput[];
  merges?: Array<{ from: string; into: string }>;
}

/**
 * The agent a screen is about.
 *
 * ONE AGENT PER SCREEN. An owner can run a boat shop in Sydney and a dental
 * practice in London off one organization, and each is one of these: its own name in the
 * title, its own clock every time on the page is printed in, its own currency and its own
 * words. `organization` is the paying customer behind it and appears only where the
 * organization is the subject - the plan panel, and the internal switcher's
 * "name — organization" option text.
 */
export interface AgentRef {
  id: string;
  name: string;
  slug: string;
  /** NULL means this business's local time is unknown; times fall back to the browser's. */
  timezone: string | null;
  currency: string;
  organizationId: string;
  organizationName: string;
  /**
   * How this business reads its dashboard: the full one, or the simplified call register -
   * large type, one call at a time - for a tablet on a counter.
   *
   * PER BUSINESS AND NOT PER PERSON, because it describes who reads the screen: the shop's
   * tablet is the shop's, whoever last signed in on it. Switched on /settings, and the only
   * page it changes is /calls.
   */
  view: "default" | "simple";
  /**
   * 'custom' for an agent that is a repository of its own, 'hosted' for one that runs on the
   * shared hosted agent from its Settings tabs. Settings draws its Instructions and Tools
   * tabs for a hosted one only - a custom agent's are its code. An operator's decision.
   */
  runtime: "custom" | "hosted";
}

/** Sunday first, matching `Date#getDay` and the Worker's `DAY_KEYS`. */
export const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
export type DayKey = (typeof DAY_KEYS)[number];

/** `["09:00", "17:00"]`; the close is exclusive and may be `24:00`. */
export type Period = [string, string];
export type Week = Record<DayKey, Period[]>;

/** Closed all day from `from` to `to`, inclusive, as local `YYYY-MM-DD` dates. */
export interface Closure {
  id: string;
  from: string;
  to: string;
  note: string;
}

/** A place the business is open to the public. Stored whole as `agent.locations_json`. */
export interface Location {
  id: string;
  name: string;
  address: string;
  hours: Week;
  closures: Closure[];
}

/**
 * One agent as `GET /v1/agents` sends it: everything its screens need to draw.
 *
 * The list is fetched once and the agent on screen is the one whose slug is in the address,
 * so the vocabulary, the timezone and the currency are read from one place rather than
 * carried on four different responses that can disagree.
 */
export interface Agent extends AgentRef {
  vocabulary: Vocabulary;
  hours: {
    locations: Location[];
    description: string;
    configured: boolean;
  };
}

export interface AgentsPage {
  agents: Agent[];
}

export interface Counted {
  key: string | null;
  calls: number;
}

export interface OverviewPage {
  window: { since: number; until: number; days: number };
  agent: AgentRef;
  vocabulary: Vocabulary;
  headline: {
    calls: number;
    callsBefore: number;
    minutes: number;
    handledEndToEnd: number;
    handledBefore: number;
    success: number;
    successBefore: number;
    handoffs: number;
    attention: number;
    graded: number;
    analysisPending: number;
    /** Contacts made in the window, and in the one before it. */
    newContacts: number;
    newContactsBefore: number;
  };
  /** One row per basis. NEVER summed across them - a quote is not a booking. */
  value: Array<{ basis: string | null; minor: number; currency: string | null; calls: number }>;
  afterHours: {
    open: number;
    closed: number;
    share: number | null;
    /** `[dow][hour]`, Sunday first: which heatmap squares count as open. */
    openSquares: boolean[][];
    description: string;
    /** False means these are OUR default hours, and the page says so. */
    configured: boolean;
    unknown: number;
  };
  when: { grid: Array<{ dow: number; hour: number; calls: number }>; unknown: number };
  intents: Counted[];
  sentiments: Counted[];
  outcomes: Array<{ label: string | null; group: string | null; calls: number }>;
  outcomesByDay: Array<{ day: number; group: string | null; calls: number }>;
  /** The same calls by week. The trend panel draws this or `outcomesByDay`, not both. */
  weeks: Array<{ week: number; calls: number; success: number }>;
  /**
   * Lead quality across the window.
   *
   * `uncaptured` is strong enquiries the call came away with no way to follow up on - the
   * one number on this page that is lost revenue and a fixable agent bug at once.
   * `measures` comes back as RAW LEVELS rather than pre-bucketed into "weak", because
   * none-or-low (they told us and the answer was thin) and unknown (nobody established it)
   * are different facts and only the second is one an agent author can fix.
   */
  qualification: {
    uncaptured: number;
    measures: Array<{ dimension: string; level: string; calls: number; example: string | null }>;
  };
  attentionCalls: Array<{
    id: number;
    startedAt: number;
    label: string | null;
    group: string | null;
    priority: string | null;
    handoff: string | null;
    resolved: boolean | null;
    headline: string | null;
    summary: string | null;
    contact: { name: string | null; location: string | null };
  }>;
}

// --- who is signed in, and what they may configure ------------------------------------

export interface Me {
  user: { email: string; name: string | null; role: "internal" | "member" };
  /** Null for an internal reader, who is not confined to one organization. */
  organization: { id: string; name: string; slug: string } | null;
}

export interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  role: "internal" | "member";
  organizationId: string | null;
  createdAt: number;
  lastLoginAt: number | null;
  disabledAt: number | null;
}

export interface IngestKey {
  id: string;
  agentId: string;
  agent: string;
  /** Not secret - it travels in the Authorization header of every ingest request. */
  prefix: string;
  label: string | null;
  createdAt: number;
  lastUsedAt: number | null;
  revokedAt: number | null;
}

/**
 * One endpoint, two bodies. The two internal blocks are ABSENT rather than empty for a
 * member - the API builds them inside a branch a member's request never enters - which is
 * why they are optional here rather than possibly-empty arrays. There was a third, the
 * audit log; it is still written on every change and is now read with `wrangler d1`.
 */
/**
 * The first sentence of every call: the owner's words, the voice they are recorded in, and
 * the recording if there is one.
 *
 * THREE FIELDS WITH THREE DIFFERENT WRITERS, which is why they are one block rather than
 * three. `text` is the owner's and this screen writes it. `voice` is ops config seeded by
 * `configure.mjs` and is shown READ-ONLY - choosing a voice means listening to voices with
 * somebody rather than typing an id into a form. `audio` is the platform's, written when
 * somebody presses Generate.
 *
 * `fallback` is what the agent says while `text` is null: the SDK's own default line with
 * this business's name in it. It is the placeholder in the empty box, so an owner who has
 * written nothing can still read their own greeting off this page.
 *
 * `stale` is computed server-side, in `domain/opening_line.ts`, and never here. It is the
 * one fact about this block that has a right answer - the agent uses the same function to
 * decide whether to play the file at all - and a second implementation in the dashboard would
 * be a screen that disagrees with what a caller hears.
 *
 * NO FIELD IS CALLED `sampleRate` OR `generatedAt`, deliberately: `redactForMember`
 * silently drops any key matching /cost|margin|micros|rate/i from a member's response,
 * twelve levels deep, so those two would simply be absent here with no error anywhere.
 */
export interface OpeningLineBlock {
  text: string | null;
  fallback: string;
  /**
   * WHETHER A VOICE IS SET UP, AND NOT WHICH ONE. It carried the provider, the voice id and
   * the model until the voice picker, and the card printed them - a vendor and a model id in
   * the middle of a panel about a sentence. The voice has its own card now (`VoiceBlock`),
   * which says "Theia · Australian · Feminine" and names nobody. All this is still doing is
   * disabling Generate with a reason when no voice has been chosen.
   */
  voice: boolean;
  audio: {
    sha256: string;
    bytes: number;
    hz: number;
    durationMs: number;
    recordedAt: number;
    stale: boolean;
  } | null;
  /** Whether this deployment has a key for that voice's vendor. False disables Generate. */
  synthesiser: boolean;
}

/**
 * The voice this business speaks in, as the settings page is told about it.
 *
 * NO VENDOR, NO MODEL, NO VOICE ID. `id` is opaque - a hash the Worker computes from what is
 * stored - and it exists so the picker can tick the row that is already in use and so the
 * card can ask for its sample clip. Everything else is English, because the person reading it
 * is choosing how their phone sounds, not configuring a service.
 *
 * `name: null` IS A REAL STATE and it is what every agent set up before the picker existed
 * looks like: the voice works, it is on the row, and nobody wrote down what it is called. The
 * card says so and still offers to change it.
 */
export interface VoiceBlock {
  id: string;
  name: string | null;
  gender: string | null;
  accent: string | null;
  traits: string[];
  /** "fleet" for an agent on the fleet's voice; "agent" once an operator picked one. */
  source: "fleet" | "agent";
}

/**
 * What the picker draws.
 *
 * `available: false` COVERS TWO SITUATIONS AND NEEDS ONE PIECE OF COPY: this deployment has
 * no voice vendor configured, and the vendor is not answering right now. Neither is the
 * owner's to fix, both leave their current voice working, and the dialog says so rather than
 * showing an empty list that reads as "there are no voices".
 */
export interface VoiceCatalogue {
  available: boolean;
  voices: Array<{
    id: string;
    name: string;
    gender: string | null;
    age: string | null;
    accent: string | null;
    traits: string[];
    /** Tagged for phone lines, or sounds like this business's own customers. Sorted first. */
    suggested: boolean;
    selected: boolean;
    /** Whether there is a sample clip to play. A row without one has Play disabled. */
    preview: boolean;
  }>;
  /** The filter chips, derived from the voices themselves rather than hard-coded. */
  accents: string[];
  genders: string[];
}

export interface SettingsPage {
  user: Me["user"];
  /**
   * The agent, and every setting on it that is editable from this screen.
   *
   * One block rather than six top-level keys, because they are saved together by
   * `POST /v1/settings/agent` and a page that read `hours` from one place and wrote it to
   * another is a page whose form can disagree with its own heading. `vocabulary` is written
   * back through `taxonomy` on the same endpoint, and the save answers with a fresh copy of
   * it so the form can re-key the rows it just added.
   */
  agent: AgentRef & {
    notifyEmail: string | null;
    description: string | null;
    hours: {
      locations: Location[];
      description: string;
      configured: boolean;
    };
    vocabulary: Vocabulary;
    /**
     * What re-reading the recent archive would cost and cover, or NULL when the
     * categories have never been edited on this screen.
     *
     * ABSENT RATHER THAN ZEROED for an unedited agent: `stale: 0` is a fact about a
     * comparison nobody made, and the page would then have to decide for itself whether
     * to draw the offer. `stale` is calls read against the older list; `pending` is
     * calls already back in the queue, which is what the progress note counts down.
     */
    reread: { changedAt: number; days: number; stale: number; pending: number } | null;
    /**
     * The fleet's voice for a row with none of its own, or the operator's pick; NULL only
     * for a row hand-edited into nothing usable, which the card has copy for. Built from
     * `voice_json` and the fleet's own snapshot, so this page costs no vendor call - the
     * catalogue is fetched only when somebody opens the picker. INTERNAL ONLY on screen.
     */
    voice: VoiceBlock | null;
    /**
     * Whether this business's calls are recorded from here on. TRUE for a new agent.
     *
     * A PRIVACY CONTROL RATHER THAN A PREFERENCE, and the panel that draws it says the
     * two things a boolean cannot: that it decides the calls from here on rather than
     * this one, and that switching it off does not delete what is already stored.
     */
    recordAudio: boolean;
    /**
     * What the phone is answered in, what it may follow a caller into, where that came
     * from, and the whole list the pickers draw from. `source` is "fleet" for an agent
     * on the fleet's list (English plus every supported language) and "agent" for an
     * operator's override. `supported` is the fleet's intersection of what its
     * recogniser, its end-of-turn model and its voice can all do. INTERNAL ONLY on
     * screen: the block is sent to every role, but only an internal reader sees the card
     * and only an internal reader's save carries the key.
     */
    languages: {
      primary: string;
      additional: string[];
      source: "fleet" | "agent";
      supported: Array<{ code: string; name: string }>;
    };
  };
  /** PER ORGANIZATION, counted across every agent the organization has. The panel says so. */
  usage: { since: number; until: number; calls: number; minutes: number; billableMinutes: number } | null;
  plan: {
    basis: "plan" | "usage" | "none";
    includedMinutes: number | null;
    overageMinutes: number | null;
    perMinute: { minor: number; currency: string } | null;
    amountDue: { minor: number; currency: string } | null;
    note: string | null;
  } | null;
  team: TeamMember[];
  agents?: Array<{ id: string; name: string; organization_id: string; organization_name: string }>;
  keys?: IngestKey[];
}


/* --- the agent's tabs on Settings ------------------------------------------------------------------------- */

/**
 * NAMES ARE VALUES, NEVER KEYS, in every type below that carries something an owner named - a
 * parameter, a header, a mapped field. `redactForMember` deletes any key matching
 * /cost|margin|micros|rate/i from a member's response, twelve levels deep, so a parameter
 * called `hourly_rate` would vanish if it were ever a key. That is why these are lists of
 * `{ name, value }` rather than records, and why a tool test answers with TEXT.
 */
export interface NameValue {
  name: string;
  value: string;
}

export type ParamType = "string" | "number" | "integer" | "boolean" | "enum" | "array";
export type ItemType = "string" | "number" | "integer" | "boolean";
export type ErrorWhen = "timeout" | "http_4xx" | "http_5xx" | "empty" | "other";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ParamFormat = "date" | "time" | "date-time" | "email" | "uuid";

export interface ToolParam {
  name: string;
  type: ParamType;
  required: boolean;
  description: string;
  values?: string[];
  items?: ItemType;
  /** A string's shape. The Worker checks it on every run, whatever the model sends. */
  format?: ParamFormat;
  /** A regular expression a string must match. No lookaround, no backreferences. */
  pattern?: string;
  /**
   * ONE PAIR, READ BY TYPE: characters for a string, items for an array, the value itself
   * for a number or an integer. The prototype's "Length / Item count / Range" is one row
   * that changes its label, and the storage is the same one pair.
   */
  min?: number;
  max?: number;
  /**
   * Filed on the caller's contact after the call: one of their own details, or `field` for a
   * detail of its own named after the parameter. Never shown to the AI.
   */
  contact?: ContactKey;
}

export type ContactKey = "name" | "phone" | "email" | "company" | "location" | "field";

export interface ToolHttp {
  connectionId: string | null;
  method: HttpMethod;
  path: string;
  query: NameValue[];
  headers: NameValue[];
  body: string;
  timeoutMs: number;
  retries: number;
}

/** A tool as it is edited and sent. The Worker validates every field of it again. */
export interface ToolInput {
  name: string;
  description: string;
  enabled: boolean;
  /**
   * OpenAI strict mode: the model must send every parameter, exactly typed, `null` for an
   * optional one the caller did not give. The Worker validates the arguments either way.
   */
  strict: boolean;
  availability: "always" | "open" | "closed";
  kind: "save" | "http" | "code";
  params: ToolParam[];
  http: ToolHttp | null;
  /** A code tool's source: a script that defines `async function run(args, ctx)`. Null otherwise. */
  code: string | null;
  mapping: Array<{ path: string; as: string }>;
  dropUnmapped: boolean;
  errors: Array<{ when: ErrorWhen; message: string }>;
  filler: string;
  fillerAfterMs: number;
  onInterrupt: "cancel" | "finish";
  notifyEmail: string;
}

export interface Tool extends ToolInput {
  id: string;
  position: number;
  /** Sent back on save, so a second tab's save cannot silently undo this one. */
  updatedAt: number;
}

/** A base address and how to sign in to it. The secret itself never comes back. */
export interface Connection {
  id: string;
  name: string;
  baseUrl: string;
  auth: "none" | "bearer" | "header" | "basic";
  headerName: string;
  username: string;
  secretSet: boolean;
  secretLast4: string | null;
  usedBy: number;
  updatedAt: number;
}

export interface ToolTestResult {
  status: "ok" | "error" | "rejected";
  durationMs: number;
  attempts: number;
  errorKind?: ErrorWhen | "invalid_args" | "unavailable";
  request?: { method: string; url: string; headers: NameValue[]; body: string | null };
  response?: { status: number; body: string; truncated: boolean };
  /** A code tool's console output and error, as text. */
  logs?: string;
  /** The line of the code its error points at. */
  errorLine?: number | null;
  /** JSON text: exactly what the model would receive. */
  modelReceives: string;
}

export interface AgentPage {
  /** How many sources the Knowledge tab holds, for its count in the Settings nav. */
  knowledgeSources?: number;
  agent: {
    id: string;
    name: string;
    runtime: "custom" | "hosted";
    revision: number;
    changedAt: number | null;
    changedBy: string | null;
    timezone: string | null;
  };
  greeting: OpeningLineBlock & { variables: Array<{ key: string; label: string }> };
  /** The next four are ABSENT for a custom agent, whose instructions and tools are code. */
  instructions?: {
    text: string;
    placeholder: string;
    /** `value`: what the detail reads as on a call starting now; null for the caller's number, which only the call knows. */
    variables: Array<{ key: string; label: string; value: string | null }>;
  };
  tools?: Tool[];
  connections?: Connection[];
  tryIt?: { available: boolean; reason?: string; remaining?: number; perHour?: number };
  /** Whether "during/outside opening hours" can be honoured: a timezone and hours are set. */
  hoursKnown: boolean;
  /**
   * The last change to ANY of this agent's settings, from the audit log - a business save
   * included, which `agent.changedAt` (the hosted config's revision) never sees.
   */
  lastChange?: { at: number; by: string | null } | null;
}

/** One run of a hosted agent's tool on a call, as the call page shows it. */
export interface ToolCall {
  toolName: string;
  kind: "save" | "http" | "code";
  status: "running" | "ok" | "error" | "rejected";
  errorKind: string | null;
  httpStatus: number | null;
  durationMs: number | null;
  configRevision: number | null;
  at: number;
  args: NameValue[];
  /** JSON text, or null for a run that never finished. */
  modelReceives: string | null;
  /** A code tool's console output and error, as text; null when there was none. */
  logs: string | null;
}

/* --- knowledge ------------------------------------------------------------------------------ */

/** One thing an agent answers from. `words` is an estimate, as is every count on the tab. */
export interface KnowledgeSource {
  id: string;
  kind: "text" | "file" | "url";
  title: string;
  url: string | null;
  /** A link: that page alone, or it and the pages under it. */
  scope: "page" | "section" | null;
  fileName: string | null;
  /** A document's size, in bytes. */
  bytes: number | null;
  /** A PDF's pages, or how many pages of a link were read. */
  pages: number | null;
  words: number;
  /**
   * `reading`: saved, and being read or made searchable right now. `failed`: not searchable yet,
   * retried. `unreadable`: a link that could not be read.
   */
  status: "ready" | "reading" | "failed" | "unreadable";
  error: string | null;
  /** In the search copy at its current text. Only meaningful when the page's mode is `search`. */
  indexed: boolean;
  createdAt: number;
  /** When the text last changed - or, for a link, when it was last read. */
  updatedAt: number;
}

/** `GET /v1/agent/knowledge?agent=`, and the answer to every write on the tab. */
export interface KnowledgePage {
  /** `inline`: handed to the agent whole. `search`: searched on every turn. */
  mode: "off" | "inline" | "search";
  words: number;
  /** Pages read across all links. */
  pages: number;
  limits: {
    sources: number;
    words: number;
    inlineWords: number;
    pagesPerLink: number;
    pages: number;
    fileBytes: number;
    wordsLabel: string;
  };
  /** Whether this deployment can hold more than `limits.inlineWords`. */
  searchAvailable: boolean;
  chunks: number;
  sources: KnowledgeSource[];
}

// --- building an agent from a website (internal) -------------------------------------------

export interface OrganizationChoice {
  id: string;
  name: string;
  agents: number;
}

export type BuildStage = "discovering" | "reading" | "writing" | "saving";

export interface BuildReport {
  pagesRead: number;
  pagesSkipped: Array<{ url: string; reason: string }>;
  dropped: Array<{ name: string; reason: string }>;
  hoursKnown: boolean;
  /** Locations saved from the site. */
  locations?: number;
  warnings: string[];
}

/** What `GET /v1/build?agent=` answers: counts and words, never a page's text. */
export interface BuildProgress {
  status: "running" | "done" | "failed";
  stage: BuildStage | null;
  websiteUrl: string;
  pagesTotal: number | null;
  pagesDone: number;
  startedAt: number | null;
  finishedAt: number | null;
  error: string | null;
  report: BuildReport | null;
}

// --- a hosted agent's public demo page ------------------------------------------------------

export interface DemoBrand {
  primary: string | null;
  primaryDeep: string | null;
  accent: string | null;
  wash: string | null;
  logoUrl: string | null;
}

export interface DemoLink {
  slug: string;
  url: string;
  state: "live" | "paused" | "expired";
  expiresAt: number;
  daysLeft: number;
  brand: DemoBrand;
  /** Where the page's logo comes from: one our team uploaded, the site's, or none. */
  logo: "uploaded" | "site" | null;
}

/**
 * Who opened a demo link (`GET /v1/agent/demo-link/visits`). `visits` are only the loads that
 * count: a browser that ran the page and was not one of ours. Everything else is a number in
 * `summary`. `summary` is null when the agent has no link.
 */
export interface DemoVisits {
  summary: {
    visits: number;
    visitors: number;
    lastVisitAt: number | null;
    /** Link-unfurl bots by whose they were: {"Slack": 2, "iMessage": 1}. */
    previews: Record<string, number>;
    /** Loads that never ran the page - most often an email scanner. */
    unconfirmed: number;
    /** Our own signed-in loads. */
    ours: number;
  } | null;
  visits: Array<{
    id: string;
    at: number;
    place: string | null;
    network: string | null;
    device: string;
    seconds: number | null;
    calledAt: number | null;
    /** The call's row, once its record has arrived; null until then. */
    call: number | null;
    /** This browser opened the link before. */
    returning: boolean;
  }>;
  /** Where the next, older page starts (`?before=`); null when this is the last one. */
  next: string | null;
}

/**
 * One person an agent has talked to: the Contacts page's row and the head of its dialog.
 *
 * `phones` are E.164 and `emails` lower case, in the order a person saved them; the first
 * phone is the one a row prints and the Call button rings. A contact is the owner's filing
 * of calls, and nothing in it reaches the agent.
 */
export interface ContactSummary {
  id: string;
  agentId: string;
  name: string | null;
  company: string | null;
  location: string | null;
  phones: string[];
  emails: string[];
  calls: number;
  lastSeenAt: number;
  addedAt: number;
  /** When to get back to them - a day where the business is, `YYYY-MM-DD` - or null. */
  followUp: { on: string; note: string | null; due: boolean } | null;
  /** One of their calls still needs a person, or their follow-up day has come. */
  waiting: boolean;
  openCalls: number;
  /**
   * What the page calls them - a person's choice, else the calls' reading, else `new` - and
   * both halves, so the dialog can say where it came from. Only a person says `customer`.
   */
  status: { shown: ContactStatus; set: ContactStatus | null; fromCalls: ContactStatus | null };
  /** Details kept on them: by an action marked "Keep on the contact", or by a person (`callId` null). */
  fields: Array<{ label: string; value: string; callId: string | null; at: number | null }>;
}

export type ContactStatus = "new" | "lead" | "customer" | "not_a_fit";

export interface ContactNote {
  id: string;
  body: string;
  by: string | null;
  /** Written by whoever is reading it. */
  mine: boolean;
  at: number;
}

export interface ContactDetail extends ContactSummary {
  notes: ContactNote[];
  /** The latest calls, as the calls list draws them. `moreCalls` when there are older ones. */
  history: CallSummary[];
  moreCalls: boolean;
}
